import { describeCard, type PaymentMethod } from '@noconflict/contracts';
import { CreditCardIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  paymentMethodErrorMessage,
  useOrgPaymentMethods,
  usePaymentMethodAdders,
  useRemovePaymentMethod,
  useSetDefaultPaymentMethod,
  useSyncPaymentMethods,
} from '../../../entities/payment-method';
import { cardPaymentsAvailable } from '../../../shared/api/stripe';
import { useFormat } from '../../../shared/i18n/format';
import { Button } from '../../../shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../shared/ui/card';
import { ConfirmDialog } from '../../../shared/ui/confirm-dialog';
import { EmptyState } from '../../../shared/ui/empty-state';
import { ErrorState } from '../../../shared/ui/error-state';
import { Notice } from '../../../shared/ui/notice';
import { Skeleton } from '../../../shared/ui/skeleton';
import { toast } from '../../../shared/ui/sonner';
import { StatusBadge } from '../../../shared/ui/status-badge';
import { AddCardDialog } from '../add-card';

/**
 * The organization's stored cards, on the Billing page. Anyone with `billing.view` sees the list; storing,
 * choosing the default and removing need `billing.manage`, and the database refuses regardless. Stripe is the
 * source of truth, so the list is refreshed from it once when the page opens.
 */
export function PaymentMethodList({
  organizationId,
  canManage,
}: {
  organizationId: string;
  canManage: boolean;
}) {
  const { t } = useTranslation('org');
  const { formatDate } = useFormat();
  const cards = useOrgPaymentMethods(organizationId);
  const sync = useSyncPaymentMethods(organizationId);
  const setDefault = useSetDefaultPaymentMethod(organizationId);
  const remove = useRemovePaymentMethod(organizationId);
  const adders = usePaymentMethodAdders(
    (cards.data ?? []).map((c) => c.added_by).filter((id): id is string => id !== null),
  );
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<PaymentMethod | null>(null);

  const available = cardPaymentsAvailable();
  // Once per visit: a card removed at Stripe should not linger here (the mutation replaces the cached list).
  const { mutate: refresh } = sync;
  useEffect(() => {
    if (canManage && available) refresh(undefined);
  }, [canManage, available, refresh]);

  const list = cards.data ?? [];
  const hasDefault = list.some((c) => c.is_default);

  return (
    <Card>
      <CardHeader>
        <CardTitle asChild className="font-sans">
          <h2>{t('settings.billing.cards.title')}</h2>
        </CardTitle>
        <CardDescription>{t('settings.billing.cards.help')}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {cards.isPending ? (
          <section
            aria-busy="true"
            aria-label={t('settings.billing.cards.loading')}
            className="grid gap-3"
          >
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-2/3" />
          </section>
        ) : null}
        {cards.isError ? (
          <ErrorState
            size="compact"
            title={t('settings.billing.cards.error')}
            description={cards.error.message}
            onRetry={() => void cards.refetch()}
          />
        ) : null}
        {!available && cards.isSuccess ? (
          <Notice tone="warning">{t('settings.billing.cards.unavailable')}</Notice>
        ) : null}

        {cards.isSuccess && list.length === 0 ? (
          <EmptyState
            size="compact"
            icon={<CreditCardIcon />}
            title={t('settings.billing.cards.emptyTitle')}
            description={t('settings.billing.cards.emptyBody')}
            action={
              canManage && available ? (
                <Button variant="accent" onClick={() => setAdding(true)}>
                  {t('settings.billing.cards.add')}
                </Button>
              ) : null
            }
          />
        ) : null}

        {list.length > 0 ? (
          <>
            {!hasDefault ? (
              <Notice tone="warning">{t('settings.billing.cards.noDefault')}</Notice>
            ) : null}
            <ul className="grid gap-3">
              {list.map((card) => {
                const adder = card.added_by ? adders.data?.[card.added_by] : undefined;
                return (
                  <li
                    key={card.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4"
                  >
                    <div className="grid gap-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-bold text-heading">{describeCard(card)}</span>
                        {card.is_default ? (
                          <StatusBadge tone="success" size="compact">
                            {t('settings.billing.cards.default')}
                          </StatusBadge>
                        ) : null}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {t('settings.billing.cards.expires', {
                          month: String(card.exp_month).padStart(2, '0'),
                          year: card.exp_year,
                        })}
                        {' · '}
                        {adder
                          ? t('settings.billing.cards.addedBy', {
                              name: adder,
                              date: formatDate(card.created_at),
                            })
                          : t('settings.billing.cards.addedOn', {
                              date: formatDate(card.created_at),
                            })}
                      </span>
                    </div>
                    {canManage ? (
                      <span className="flex flex-wrap gap-2">
                        {card.is_default ? null : (
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled={setDefault.isPending}
                            onClick={async () => {
                              try {
                                await setDefault.mutateAsync(card.id);
                                toast.success(t('settings.billing.cards.defaultChanged'));
                              } catch (e) {
                                toast.error(paymentMethodErrorMessage(e));
                              }
                            }}
                          >
                            {t('settings.billing.cards.makeDefault')}
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => setRemoving(card)}>
                          {t('settings.billing.cards.remove')}
                        </Button>
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            {canManage && available ? (
              <div>
                <Button variant="secondary" onClick={() => setAdding(true)}>
                  {t('settings.billing.cards.add')}
                </Button>
              </div>
            ) : null}
          </>
        ) : null}
      </CardContent>

      <AddCardDialog organizationId={organizationId} open={adding} onOpenChange={setAdding} />
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(next) => !next && setRemoving(null)}
        title={t('settings.billing.cards.removeTitle')}
        description={t('settings.billing.cards.removeBody', {
          card: removing ? describeCard(removing) : '',
        })}
        confirmLabel={t('settings.billing.cards.removeConfirm')}
        cancelLabel={t('settings.billing.cards.removeCancel')}
        errorMessage={paymentMethodErrorMessage}
        onConfirm={async () => {
          if (!removing) return;
          await remove.mutateAsync(removing.id);
          toast.success(t('settings.billing.cards.removed'));
          setRemoving(null);
        }}
      />
    </Card>
  );
}
